import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import autobind from 'core-decorators/lib/autobind';
import debounce from 'core-decorators/lib/debounce';
import { connect } from 'react-redux';
import ui from 'redux-ui';

import { getNewRelic } from 'shared/analytics/new-relic';

import {
  isClassicLayoutHeaderVisible,
  isClassicLayoutSearchBarVisible,
  isSearchInputVisible,
  isCategoriesDropdownVisible,
} from 'shared/utils/layout-helpers';
import { WixConnect } from 'shared/utils/wix-connect';
import WixSDK from 'shared/utils/wix-sdk';
import { getVideoSelectedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { storeForReload } from 'widget/utils/reload';
import paymentsHelper from 'widget/utils/payments';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import {
  openFullScreenOverlay,
  openFullScreenSubscribeOverlay,
  openFullScreenMemberOnlyOverlay,
} from 'shared/utils/open-overlay';
import {
  listThumbnailSize,
  videosPerPage,
  classicLayoutWidgetHeight,
  MIN_WIDGET_WIDTH,
  MAX_WIDGET_WIDTH,
} from 'widget/utils/videos-sizes';


import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo } from 'widget/redux/client/actions/change-playback-status';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { getAllSettings } from 'shared/selectors/app-settings';
import { isVideoPlaying, isVideoPaused } from 'widget/selectors/video-playback-status';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';

import viewModes from 'shared/constants/view-modes';

import BaseView from 'widget/views/_base';

import DesktopPlayer from 'widget/components/players/desktop-player';
import MobilePlayer from 'widget/components/players/mobile-player';
import { isPreviewMode, openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';
import ShareChannel from 'widget/components/share-channel/share-channel';
import AccountButton from 'widget/components/account/account-button';
import ChannelInfoButton from 'widget/components/channel-actions/channel-info-button';
import ChannelSubscriptionButton from 'widget/components/channel-actions/channel-subscription-button';
import Categories from 'widget/components/categories/categories';
import Tags from 'widget/components/tags/tags';
import { ClassicSearchBar } from 'widget/components/search-bar/search-bar';
import VideosListClassic from 'widget/components/videos-list/classic/classic';
import OverlayClassicCenter from 'widget/components/player-overlay/classic/center';
import OverlayMemberOnly from 'widget/components/player-overlay/classic/member-only';
import { WidgetScrollbarWrapper } from 'shared/components/scrollbar-wrapper/scrollbar-wrapper';
import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import EmptySearchResults from 'widget/components/empty-search-results/empty-search-results';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';

import styles from './classic.styl';

import widgetTranslations from 'widget/translations';

const DEFAULT_STATE = {
  canShowChannelCover: true,
  isInFullScreen: false,
  isTagFocused: false,
  isSearchInputFocused: false,
  searchLayout: false,
  currentCategory: null,
};


@connect((state, ownProps) => ({
  mainVideoId: getMainVideoId(state, DATA_KEY),
  isVideoPlaying: isVideoPlaying(state),
  isVideoPaused: isVideoPaused(state),
  currentChannelId: state.currentChannelId,
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  firstChannelVideoId: state.firstChannelVideoId,
  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  isSearchBarVisible: isClassicLayoutSearchBarVisible(state.appSettings, ownProps.channelData),
  isSearchInputVisible: isSearchInputVisible(state.appSettings, ownProps.channelData),
  isCategoriesDropdownVisible: isCategoriesDropdownVisible(state.appSettings, ownProps.channelData),
}), {
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
})
@ui()
@WixConnect(styles)
export default class ClassicView extends BaseView {

  static displayName = 'ClassicView';

  static propTypes = {
    isSearchBarVisible: React.PropTypes.bool,
    isSearchInputVisible: React.PropTypes.bool,
    isCategoriesDropdownVisible: React.PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...DEFAULT_STATE,
      searchBarValue: props.ui.searchQuery,
    };

    getNewRelic().setCustomAttribute('layout', 'classic');
  }

  componentDidMount() {
    super.componentDidMount();
    this.setUtmostWidgetSize();
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);

    const { appSettings, channelData } = this.props;

    if (
      appSettings.numbers.videosInRow !== prevProps.appSettings.numbers.videosInRow ||
      appSettings.numbers.videoInfoPosition !== prevProps.appSettings.numbers.videoInfoPosition ||
      !_.isEqual(appSettings.booleans, prevProps.appSettings.booleans) ||
      channelData !== prevProps.channelData ||
      !_.isEqual(appSettings.requestedWidgetSize, prevProps.appSettings.requestedWidgetSize)
    ) {
      this.setUtmostWidgetSize();
    }
  }

  get listThumbnailSize() {
    const { appSettings } = this.props;
    const widgetWidth = appSettings.requestedWidgetSize.width;
    const videosInRow = appSettings.numbers.videosInRow;
    return listThumbnailSize(widgetWidth, videosInRow);
  }

  // TODO: move to data component
  @autobind
  @debounce
  setUtmostWidgetSize() {
    const { appSettings, channelData } = this.props;

    if (!appSettings || !channelData) {
      return;
    }

    const videosInRow = appSettings.numbers.videosInRow;
    const initialWidth = window.innerWidth;
    const initialHeight = window.innerHeight;
    const width = Math.min(Math.max(initialWidth, MIN_WIDGET_WIDTH), MAX_WIDGET_WIDTH);
    let height = initialHeight;

    const allVideosCount = channelData.videosCount;

    // what videos rows need to be displayed
    const perPage = videosPerPage({
      width,
      height,
    }, videosInRow, allVideosCount, appSettings, channelData);
    let rowsCount = Math.ceil(Math.min(perPage, allVideosCount) / videosInRow);

    if (rowsCount <= 0) {
      rowsCount = 1;
    }

    // widget height for this rows count
    height = Math.ceil(classicLayoutWidgetHeight(
      width, videosInRow, allVideosCount, rowsCount, appSettings, channelData,
    ));

    if (width !== initialWidth || height !== initialHeight) {
      if (WixSDK.Utils.getViewMode() === viewModes.EDITOR) {
        // editor
        WixSDK.resizeComponent({ width, height });
      } else {
        // viewer
        WixSDK.resizeWindow(width, height);
      }
      // fix TPA bug. TODO remove
      WixSDK.setHeight(height);
    }
  }

  @autobind
  reset() {
    super.reset();

    this.setState({
      ...DEFAULT_STATE,
      searchBarValue: '',
    });
    this.mainVideoList.toFirstPage();
  }

  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;

    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  @autobind
  handleThumbnailClick(videoItem) {
    this.scrollToWidgetTop();
    this.selectVideo(videoItem);
    this.sendVidSelectedBiEvent(videoItem);
  }

  @autobind
  scrollToWidgetTop() {
    const { appSettings: { compId } } = this.props;
    WixSDK.navigateToComponent(compId);
  }

  get tagList() {
    return _.get(this.props, 'channelData.statsInfo.tags') || [];
  }

  get categoriesList() {
    const categoriesStats = _.get(this.props, 'channelData.statsInfo.categories') || [];
    return _.map(categoriesStats, 'value');
  }

  get tags() {
    const { searchLayout, searchBarValue } = this.state;
    const tags = this.tagList;

    if (!tags.length || !searchLayout || searchBarValue) {
      return null;
    }

    return (
      <div styleName="tags-wrapper">
        <WidgetScrollbarWrapper>
          <Tags styleName="tags"
                onTagFocus={this.handleTagsFocus}
                onTagBlur={this.handleTagsBlur}
                onTagClick={this.searchByTag}
                tags={tags} />
        </WidgetScrollbarWrapper>
      </div>
    );
  }

  get noSearchResults() {
    const { searchBarValue, searchLayout } = this.state;

    if (!this.hasNoSearchResults || (searchLayout && !searchBarValue)) {
      return null;
    }

    return <EmptySearchResults />;
  }

  get videosList() {
    const { paginatedChannelVideos, channelData, ui: { searchQuery }, mainVideoId, isVideoPlaying } = this.props;
    const { searchLayout } = this.state;
    const searchBlockShown = searchLayout && !searchQuery && this.tagList.length;

    const noVideos = !paginatedChannelVideos.ids.length;
    if (!this.isFetching && channelData.videosCount === 0) {
      return (
        <div styleName="coming-soon">
          <FormattedMessage {...widgetTranslations.thisChannelIsComingSoon} />
        </div>
      );
    }

    if (this.isFetching || noVideos || !this.mainVideo || searchBlockShown) {
      return null;
    }

    return (
      <VideosListClassic onThumbnailClick={this.handleThumbnailClick}
                         thumbnailSize={this.listThumbnailSize}
                         paginatedChannelVideos={paginatedChannelVideos}
                         isSelectedVideoIndicationDisabled={this.isChannelCoverShown}
                         isVideoPaused={!isVideoPlaying}
                         selectedVideoId={mainVideoId}
                         styleName="videos" />
    );
  }

  get search() {
    const { isSearchInputVisible, ui: { searchQuery } } = this.props;

    if (!isSearchInputVisible) {
      return null;
    }

    const value = this.isCategorySelected ? '' : searchQuery;

    return (
      <ClassicSearchBar value={value}
                        onFocus={this.handleSearchInputFocus}
                        onBlur={this.handleSearchInputBlur}
                        onChange={this.setSearchBarValue}
                        onSearch={this.searchByQuery}
                        onClose={this.clearSearch}
                        styleName="search" />
    );
  }

  get categories() {
    const { isCategoriesDropdownVisible, ui } = this.props;

    if (!isCategoriesDropdownVisible) {
      return null;
    }

    const { searchLayout, currentCategory, searchBarValue } = this.state;

    if (
      searchLayout ||
      (!this.isCategorySelected && (searchBarValue || ui.searchQuery))
    ) {
      return null;
    }

    return (
      <Categories styleName="categories"
                  list={this.categoriesList}
                  onCategorySelect={this.searchByCategory}
                  selectedCategory={currentCategory} />
    );
  }

  get content() {
    const styleNames = classnames('content', {
      'no-search-bar': !this.props.isSearchBarVisible,
    });

    return (
      <section styleName={styleNames}
               className="qa-widget-content">
        <div styleName="search-line"
             className="qa-widget-search-line">
          {this.search}
          {this.categories}
        </div>
        {this.tags}
        {this.videosList}
        {this.noSearchResults}
      </section>
    );
  }

  get channelTitle() {
    const { channelData, appSettings } = this.props;
    return appSettings.booleans.showChannelTitle ? channelData.title : null;
  }

  @autobind
  showChannelInfo(event) {
    event.preventDefault();
    openFullScreenOverlay(`#/channel/${this.props.channelData.id}/info`);
  }

  @autobind
  openSubscription() {
    if (isPreviewMode()) {
      openPreviewPrompt();
      return;
    }

    openFullScreenSubscribeOverlay(this.props.channelData.id, () => {
      this.props.biEvent('widget.subscriptionPopUp.Completed', { result: 'canceled', errorMsg: '' });
    });
  }

  @autobind
  openMemberOnly(id) {
    openFullScreenMemberOnlyOverlay(this.props.channelData.id, id);
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      mainVideoId: this.props.mainVideoId,
    });
  }

  @autobind
  processDataFromStorage({ selectedVideoId, autoplay }) {
    if (autoplay) {
      this.playVideo({ id: selectedVideoId });
    } else {
      this.selectVideo({ id: selectedVideoId });
    }
  }

  get header() {
    const { appSettings, channelData } = this.props;
    const styleNames = classnames('header', {
      'empty-header': !isClassicLayoutHeaderVisible(appSettings, channelData),
    });
    const channelTitle = this.channelTitle;

    return (
      <section styleName={styleNames}
               className="qa-widget-header">
        <div styleName="channel-name"
             title={channelTitle}>
          {channelTitle}
        </div>
        <ShareChannel styleName="action"
                      channelData={channelData} />
        <ChannelInfoButton styleName="action"
                           channelData={channelData}
                           onClick={this.showChannelInfo} />
        <ChannelSubscriptionButton styleName="action"
                                   channelData={channelData}
                                   onClick={this.openSubscription} />
        <AccountButton styleName="action"
                       channelData={channelData}
                       onUserLogin={this.storeDataForReload}
                       onUserLogout={this.storeDataForReload}
                       onSubscribeClick={this.openSubscription}
                       onSubscriptionCancelClick={this.processSubscriptionCancel} />
      </section>
    );
  }

  get player() {
    const videoItem = this.mainVideo;

    if (videoItem) {
      return this.renderPlayerForCurrentDevice(videoItem);
    }
    return this.noVideosPlayerContent;
  }

  renderPlayerForCurrentDevice(videoItem) {
    const { channelData, isVideoPlaying } = this.props;
    const commonProps = {
      styleName: 'player',
      className: 'qa-widget-player',
      videoItem,
      channelData,
      ...this.playerSize,
      paused: !isVideoPlaying,
      canShowChannelCover: this.canShowChannelCover,
      onPurchaseClick: this.openPurchase,
      onSubscribeClick: this.openSubscription,
      openPreviewPrompt,
      onMemberSignUp: this.openMemberOnly,
      memberOnlyOverlay: <OverlayMemberOnly />,
      children: this.playerOverlay,
      onFullScreenChanged: this.setFullScreenState,
    };

    const PlayerComponent = isMobile() || isTablet() ?
      MobilePlayer :
      DesktopPlayer;

    return <PlayerComponent {...commonProps} />;
  }

  get noVideoPlayerOverlay() {
    const { appSettings, channelData } = this.props;

    const props = {
      appSettings,
      channelData,
      showChannelCover: true,
      videoItem: {},
    };

    return (
      <OverlayClassicCenter {...props} />
    );
  }

  get playerOverlay() {
    const videoItem = this.mainVideo;
    const { appSettings, channelData } = this.props;
    const props = {
      appSettings,
      channelData,
      videoItem,
      showChannelCover: this.canShowChannelCover,
      nextVideoId: this.props.nextVideoId,
      playSelectedVideo: this.playSelectedVideo,
      playNextVideo: this.playNextVideo,
    };

    return (
      <OverlayClassicCenter {...props} />
    );
  }

  sendVidSelectedBiEvent({ id }) {
    const { appSettings, channelData, videoByIds } = this.props;
    this.props.biEvent(
      'widget.VidSelected.displayed',
      getVideoSelectedEventParams(appSettings, channelData, videoByIds[id], this.canShowChannelCover),
    );
  }

  get isVideosLoaded() {
    const { videoByIds, paginatedChannelVideos } = this.props;
    return !paginatedChannelVideos.isFetching || !_.isEmpty(videoByIds);
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { appSettings } = this.props;
    const widgetSize = _.get(appSettings, 'requestedWidgetSize');
    const styleNames = classnames('container', `view-mode-${appSettings.editMode}`);

    return (
      <section styleName={styleNames}
               style={widgetSize}
               data-channel-layout="classic">
        <UpdateWidgetSize disabled={this.state.isInFullScreen} />
        <ReloadStorage onReload={this.processDataFromStorage} />

        {this.header}
        {this.player}
        {this.content}

        <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                       onSale={this.setCurrentVideoFromPayment}
                       onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/classic/classic.js